import { Link } from 'react-router-dom';
import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NJBadge, NJButton, NJCard, NJCardBody, NJHeading, NJIconButton, NJMenu, NJText, NJTooltip } from '@engie-group/fluid-design-system-react';
import type { DiscoveredPrototype } from '../shared/discoverPrototypes';
import { fetchCommentStats, getPrototypeCommentStats, type CommentStats } from '../shared/bffComments';
import { EDITABLE_STATUSES, isEditableStatus, type PrototypeStatus } from '../shared/prototypeStatus';
import { useStudioState } from './home/useStudioState';
import { STATUS_BADGE } from './home/constants';
import { comparePrototypesByUpdatedAt, formatUpdatedAt } from '../shared/prototypeDates';
import { UM_REGISTRY } from '../prototypes/urbanmap/_registry';
import type { UMRegistryEntry } from '../prototypes/urbanmap/_registry';

interface UrbanMapHomeProps {
  prototypes: DiscoveredPrototype[];
}

const PREVIEW_MODULES = import.meta.glob<{ default: string }>(
  '../prototypes/urbanmap/*/preview.png',
  { eager: true },
);

// ─── Cards ────────────────────────────────────────────────────────────────────

const STATUS_VARIANTS = {
  reference: 'success',
  delivered: 'information',
  ready: 'success',
  review: 'warning',
  draft: 'neutral',
  archived: 'neutral',
} as const;

function clampedTextStyle(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
  };
}

function PrototypeCard({
  proto, status, onChangeStatus, commentStats, commentsError,
  isArchived, isDeleted, onArchive, onRestoreArchive, onDelete, onRestoreDelete,
}: {
  proto: DiscoveredPrototype;
  status: PrototypeStatus;
  onChangeStatus: (status: Exclude<PrototypeStatus, 'archived'>) => void;
  commentStats?: CommentStats;
  commentsError: boolean;
  isArchived: boolean;
  isDeleted: boolean;
  onArchive: () => void;
  onRestoreArchive: () => void;
  onDelete: () => void;
  onRestoreDelete: () => void;
}) {
  const [menuOpened, setMenuOpened] = useState(false);
  const previewSrc = PREVIEW_MODULES[`../prototypes/urbanmap/${proto.slug}/preview.png`]?.default;
  return (
    <NJCard hasBottomBorder>
      <div style={{ position: 'relative', flexShrink: 0 }}>
      <Link
        to={`/urbanmap/prototype/${proto.slug}`}
        aria-label={`Prévisualiser ${proto.meta.title}`}
        style={{ display: 'block', aspectRatio: '8 / 5', overflow: 'hidden' }}
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={`Aperçu — ${proto.meta.title}`}
            loading="lazy"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }}
          />
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
            background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
          }}>
            <NJText scale="sm" variant="secondary">Aperçu non disponible</NJText>
          </div>
        )}
      </Link>
          <div style={{
            position: 'absolute',
            top: 'var(--nj-semantic-size-spacing-8)',
            right: 'var(--nj-semantic-size-spacing-8)',
            display: 'inline-flex',
          }}>
            {isArchived || isDeleted ? (
              <NJBadge variant="neutral" emphasis="subtle">{isDeleted ? 'Corbeille' : 'Archivé'}</NJBadge>
            ) : (
              <label style={{ position: 'relative', display: 'inline-flex' }}>
                <NJBadge variant={STATUS_VARIANTS[status]} emphasis="subtle">
                  {STATUS_BADGE[status].label}
                  <NJText as="span" aria-hidden="true"> ▾</NJText>
                </NJBadge>
                <select
                  aria-label={`Statut de ${proto.meta.title}`}
                  value={status}
                  onChange={(event) => {
                    if (isEditableStatus(event.target.value)) onChangeStatus(event.target.value);
                  }}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                >
                  {EDITABLE_STATUSES.map((value) => (
                    <option key={value} value={value}>{STATUS_BADGE[value].label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
      </div>
      <NJCardBody style={{ display: 'flex', flex: 1, minHeight: 0, padding: 'var(--nj-semantic-size-spacing-16)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 'var(--nj-semantic-size-spacing-12)' }}>
          <Link to={`/urbanmap/prototype/${proto.slug}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
            <NJHeading as="h3" scale="sm" style={{ margin: '0 0 var(--nj-semantic-size-spacing-8)' }}>
              <NJText as="span" scale="sm" style={clampedTextStyle(2)}>
                {proto.meta.title}
              </NJText>
            </NJHeading>
            <NJTooltip text={proto.meta.description} placement="top" hasArrow>
              <NJText as="span" scale="sm" style={clampedTextStyle(2)} tabIndex={0}>
                {proto.meta.description}
              </NJText>
            </NJTooltip>
          </Link>
          <div aria-live="polite">
            {commentsError ? (
              <NJText scale="sm" variant="secondary">Statistiques indisponibles</NJText>
            ) : !commentStats ? (
              <NJText scale="sm" variant="secondary">Chargement des discussions…</NJText>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-8)' }}>
                <NJBadge variant={commentStats.open ? 'warning' : 'neutral'} emphasis="subtle">
                  {commentStats.open} discussion{commentStats.open !== 1 ? 's' : ''} ouverte{commentStats.open !== 1 ? 's' : ''}
                </NJBadge>
                <NJText as="span" scale="xs" variant="secondary">
                  {commentStats.resolved} résolue{commentStats.resolved !== 1 ? 's' : ''}
                </NJText>
              </div>
            )}
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', gap: 'var(--nj-semantic-size-spacing-8)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <NJText scale="xs" variant="secondary">Créé par : {proto.meta.author || 'Non renseigné'}</NJText>
              <NJText scale="xs" variant="secondary">Dernière modification : {formatUpdatedAt(proto.meta.updatedAt)}</NJText>
            </div>
            <NJMenu.Root opened={menuOpened} onOpen={setMenuOpened}>
              <NJMenu.Anchor>
                <NJIconButton label={`Actions de ${proto.meta.title}`} title="Actions" icon="more_vert" variant="tertiary" scale="sm" />
              </NJMenu.Anchor>
              <NJMenu.Dropdown aria-label={`Actions de ${proto.meta.title}`}>
                {isDeleted ? (
                  <NJMenu.Item label={`Rétablir ${proto.meta.title}`} onClick={() => {
                    setMenuOpened(false);
                    onRestoreDelete();
                  }}>
                    Rétablir le prototype
                  </NJMenu.Item>
                ) : (
                  <>
                    <NJMenu.Item label={`${isArchived ? 'Rétablir' : 'Archiver'} ${proto.meta.title}`} onClick={() => {
                      setMenuOpened(false);
                      isArchived ? onRestoreArchive() : onArchive();
                    }}>
                      {isArchived ? 'Rétablir des archives' : 'Archiver'}
                    </NJMenu.Item>
                    <NJMenu.Item label={`Mettre ${proto.meta.title} à la corbeille`} onClick={() => {
                      setMenuOpened(false);
                      onDelete();
                    }}>
                      Mettre à la corbeille
                    </NJMenu.Item>
                  </>
                )}
              </NJMenu.Dropdown>
            </NJMenu.Root>
          </div>
        </div>
      </NJCardBody>
    </NJCard>
  );
}

function RegistryCard({ entry }: { entry: UMRegistryEntry }) {
  const isComponent = entry.type === 'component';
  const linkTo = isComponent
    ? `/urbanmap/component/${entry.slug}`
    : `/urbanmap/page/${entry.slug}`;

  const cardStyle: React.CSSProperties = {
    padding: 16,
    borderRadius: 8,
    border: `1px solid ${isComponent ? '#dbeafe' : '#d1fae5'}`,
    background: isComponent ? '#f0f9ff' : '#ecfdf5',
    transition: 'box-shadow 0.15s',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    cursor: 'pointer',
  };

  return (
    <Link
      to={linkTo}
      style={cardStyle}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '2px 6px', borderRadius: 4,
          background: isComponent ? '#bfdbfe' : '#a7f3d0',
          color: isComponent ? '#1e40af' : '#065f46',
        }}>
          {isComponent ? 'Composant' : 'Page'}
        </span>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{entry.title}</h3>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{entry.description}</p>
      <code style={{ display: 'block', marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
        {entry.path}
      </code>
    </Link>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function UrbanMapHome({ prototypes }: UrbanMapHomeProps) {
  const sortedPrototypes = [...prototypes].sort(comparePrototypesByUpdatedAt);
  const studio = useStudioState();
  const [openOnly, setOpenOnly] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const comments = useQuery({
    queryKey: ['comment-stats'],
    queryFn: fetchCommentStats,
    enabled: studio.ready && prototypes.length > 0,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const components = UM_REGISTRY.filter((e) => e.type === 'component');
  const pages = UM_REGISTRY.filter((e) => e.type === 'page');
  const isArchived = (proto: DiscoveredPrototype) => studio.isArchived(proto.slug) ||
    (proto.meta.status === 'archived' && !studio.isUnarchived(proto.slug));
  const activePrototypes = sortedPrototypes.filter((proto) => !studio.isDeleted(proto.slug) && !isArchived(proto));
  const archivedPrototypes = sortedPrototypes.filter((proto) => !studio.isDeleted(proto.slug) && isArchived(proto));
  const deletedPrototypes = sortedPrototypes.filter((proto) => studio.isDeleted(proto.slug));
  const statsFor = (slug: string) => comments.data && getPrototypeCommentStats(comments.data, slug);
  const prototypesWithOpenDiscussions = activePrototypes.filter((proto) => (statsFor(proto.slug)?.open ?? 0) > 0);
  const visiblePrototypes = openOnly ? prototypesWithOpenDiscussions : activePrototypes;
  const totals = activePrototypes.reduce((total, proto) => {
    const stats = statsFor(proto.slug);
    total.open += stats?.open ?? 0;
    total.resolved += stats?.resolved ?? 0;
    return total;
  }, { open: 0, resolved: 0 });
  const mutate = async (action: () => Promise<void>) => {
    setMutationError(null);
    try {
      await action();
    } catch (error) {
      setMutationError(`Impossible de mettre à jour le prototype : ${error instanceof Error ? error.message : String(error)}`);
    }
  };
  const renderCards = (items: DiscoveredPrototype[]) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 'var(--nj-semantic-size-spacing-16)' }}>
      {items.map((proto) => {
        const override = studio.getStatus(proto.slug);
        const original = proto.meta.status === 'archived' ? 'draft' : proto.meta.status ?? 'draft';
        const status = isEditableStatus(override) ? override : original;
        return <PrototypeCard
          key={proto.slug}
          proto={proto}
          status={status}
          onChangeStatus={(value) => {
            void mutate(() => studio.setStatus(proto.slug, value === original ? null : value));
          }}
          commentStats={statsFor(proto.slug)}
          commentsError={comments.isError}
          isArchived={isArchived(proto)}
          isDeleted={studio.isDeleted(proto.slug)}
          onArchive={() => { void mutate(() => studio.setArchived(proto.slug, true)); }}
          onRestoreArchive={() => { void mutate(() => studio.setArchived(proto.slug, false)); }}
          onDelete={() => { void mutate(() => studio.setDeleted(proto.slug, true)); }}
          onRestoreDelete={() => { void mutate(() => studio.setDeleted(proto.slug, false)); }}
        />;
      })}
    </div>
  );

  return (
    <div style={{ padding: 40, maxWidth: 1000, margin: '0 auto' }}>
      <Link
        to="/"
        title="Retour à l'accueil Prototyper Studio (choix Size2Mix / Urban Map)"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          marginBottom: 16, padding: '4px 8px', borderRadius: 6,
          color: '#64748b', textDecoration: 'none', fontSize: 12, fontWeight: 500,
          background: '#f1f5f9', border: '1px solid #e2e8f0',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#e2e8f0'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#f1f5f9'; }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>home</span>
        Accueil
      </Link>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Urban Map — Studio</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>
        Prototypes et composants réutilisables pour l'application UrbanMap.
      </p>

      {mutationError && <NJText role="alert">{mutationError}</NJText>}
      {studio.ready && prototypes.length > 0 && (
        <section aria-label="Suivi des discussions" style={{ marginBottom: 'var(--nj-semantic-size-spacing-24)' }}>
          <NJHeading as="h2" scale="sm">Discussions des prototypes actifs</NJHeading>
          <div aria-live="polite">
            {comments.isError ? (
              <NJText role="alert">Impossible de charger les statistiques des commentaires.</NJText>
            ) : comments.isPending ? (
              <NJText variant="secondary">Chargement des statistiques des commentaires…</NJText>
            ) : (
              <NJText>
                {totals.open} discussion{totals.open !== 1 ? 's' : ''} ouverte{totals.open !== 1 ? 's' : ''}
                {' · '}{prototypesWithOpenDiscussions.length} prototype{prototypesWithOpenDiscussions.length !== 1 ? 's' : ''} à traiter
                {' · '}{totals.resolved} discussion{totals.resolved !== 1 ? 's' : ''} résolue{totals.resolved !== 1 ? 's' : ''}
              </NJText>
            )}
          </div>
          <NJText scale="sm" variant="secondary">
            Sous-vues incluses ; les réponses ne sont pas comptées comme de nouvelles discussions.
          </NJText>
          <div role="group" aria-label="Filtrer par discussions" style={{
            display: 'flex', flexWrap: 'wrap', gap: 'var(--nj-semantic-size-spacing-8)',
            marginTop: 'var(--nj-semantic-size-spacing-12)',
          }}>
            <NJButton label="Tous les prototypes" scale="sm" variant={openOnly ? 'secondary' : 'primary'}
              aria-pressed={!openOnly} onClick={() => setOpenOnly(false)} />
            <NJButton label="Questions ouvertes" scale="sm" variant={openOnly ? 'primary' : 'secondary'}
              aria-pressed={openOnly} onClick={() => setOpenOnly(true)}
              disabled={comments.isPending || comments.isError} />
            <NJButton label={comments.isError ? 'Réessayer' : 'Actualiser les statistiques'} scale="sm" variant="secondary" emphasis="minimal"
              onClick={() => { void comments.refetch(); }} disabled={comments.isFetching} />
          </div>
        </section>
      )}

      {/* ─── Prototypes ─── */}
      <section aria-label="Prototypes" style={{ marginBottom: 'var(--nj-semantic-size-spacing-48)' }}>
        <NJHeading as="h2" scale="md">
          Prototypes ({visiblePrototypes.length})
        </NJHeading>
        <NJText scale="sm" variant="secondary" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
          Maquettes interactives cliquables.
          {' '}Discussions ouvertes et résolues, sous-vues incluses ; les réponses ne sont pas comptées séparément.
        </NJText>
        {!studio.ready ? (
          <NJText variant="secondary">Chargement des prototypes…</NJText>
        ) : activePrototypes.length === 0 ? (
          <NJText variant="secondary">Aucun prototype pour le moment.</NJText>
        ) : openOnly && comments.isError ? (
          <NJText variant="secondary">Filtrage indisponible : réessayez le chargement ou affichez tous les prototypes.</NJText>
        ) : visiblePrototypes.length === 0 ? (
          <NJText variant="secondary">Aucun prototype actif avec une discussion ouverte.</NJText>
        ) : (
          renderCards(visiblePrototypes)
        )}
      </section>
      {studio.ready && (
        <>
          <section aria-label="Archives" style={{ marginBottom: 'var(--nj-semantic-size-spacing-40)' }}>
            <NJHeading as="h2" scale="md" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
              Archives ({archivedPrototypes.length})
            </NJHeading>
            {archivedPrototypes.length ? renderCards(archivedPrototypes) : (
              <NJText variant="secondary">Aucun prototype archivé.</NJText>
            )}
          </section>
          <section aria-label="Corbeille" style={{ marginBottom: 'var(--nj-semantic-size-spacing-40)' }}>
            <NJHeading as="h2" scale="md" style={{ marginBottom: 'var(--nj-semantic-size-spacing-16)' }}>
              Corbeille ({deletedPrototypes.length})
            </NJHeading>
            {deletedPrototypes.length ? renderCards(deletedPrototypes) : (
              <NJText variant="secondary">Aucun prototype dans la corbeille.</NJText>
            )}
          </section>
        </>
      )}

      {/* ─── Composants & Pages de base ─── */}
      <section>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: '#334155' }}>
          Composants &amp; Pages de base
        </h2>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Briques réutilisables pour construire des prototypes. Importez-les depuis <code>../_components</code> ou <code>../_pages</code>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {pages.map((entry) => <RegistryCard key={entry.slug} entry={entry} />)}
          {components.map((entry) => <RegistryCard key={entry.slug} entry={entry} />)}
        </div>
      </section>
    </div>
  );
}
